import { useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import { SelectBuyerList } from "components/form_components/BuyerListSelect";
import { BuyerSelect } from "components/form_components/BuyerSelect";
import { BuyerTypeSelect } from "components/form_components/BuyerTypeSelect";
import { CountryRegionSelect } from "components/form_components/CountryRegionSelect";
import { DateRange } from "components/form_components/Inputs";
import { SupplierSelect } from "components/form_components/SupplierSelect";
import { useFormAutoSubmit } from "lib/hooks/useFormAutoSubmit";
import {
  EMPTY_SPEND_FILTER_FIELDS,
  omitUnsetSpendFilters,
  SpendDataFilters as Filters,
} from "./hooks/useSpendUrlState";

type Props = {
  filters: Filters;
  setFilters: (newFilters: Filters) => void;
};

/** The spend filter form. Changes apply as they are made; the panel around it owns the chrome. */
function SpendDataFilters({ setFilters: handleChange, filters }: Props) {
  const { control, watch, getValues, trigger, reset } = useForm<Filters>({
    defaultValues: filters,
    mode: "onChange",
  });

  const data = watch();

  // The page stores an unset filter as an absent key, so drop the form's empty values
  // rather than sending up empty arrays, strings, false flags and emptied locations.
  const publishFilters = useCallback(
    (values: Filters) => handleChange(omitUnsetSpendFilters(values)),
    [handleChange],
  );

  useFormAutoSubmit({
    watch,
    getValues,
    trigger,
    onChange: publishFilters,
  });

  // The parent owns the filters, so mirror them whenever they change. Filling the gaps
  // with EMPTY_SPEND_FILTER_FIELDS is what empties the panel on "Clear all": a filter the
  // new set doesn't mention would otherwise stay on screen and be resubmitted by the next edit.
  useEffect(() => {
    reset({ ...EMPTY_SPEND_FILTER_FIELDS, ...filters });
  }, [filters, reset]);

  return (
    <form aria-label="Spend filters">
      <Section>
        <SectionTitle as="h3" fontWeight="600" color="sysTextDefault">
          Transaction date
        </SectionTitle>
        <DateRange name="dateRange" control={control} label="" />
      </Section>

      <Section>
        <SectionTitle as="h3" fontWeight="600" color="sysTextDefault">
          Buyers
        </SectionTitle>
        <BuyerSelect
          name="buyers"
          label=""
          control={control}
          mode="multiple"
          placeholder="Search buyer name"
          disabled={data.buyerLists && data.buyerLists.length > 0}
          allowClear
        />
        <Divider />
        <BuyerTypeSelect
          name="buyerCategories"
          control={control}
          multiple
          label="Buyer type"
          placeholder="Select"
          allowClear
        />
        <Flex column gap={8}>
          <CountryRegionSelect
            name="buyerCountryRegions"
            control={control}
            label="Buyer HQ location"
            allowClear
          />
          <Text description color="sysTextSecondary">
            Based on where the buyer is headquartered, so national bodies appear where their HQ is.
            Add a buyer type to see e.g. councils in the North West. A few buyers don't have a
            location yet.
          </Text>
        </Flex>
        <SelectBuyerList
          name="buyerLists"
          label="Buyer lists"
          control={control}
          placeholder="Select"
          mode="multiple"
          disabled={data.buyers && data.buyers.length > 0}
          allowClear
        />
      </Section>

      <Section>
        <SectionTitle as="h3" fontWeight="600" color="sysTextDefault">
          Suppliers
        </SectionTitle>
        <SupplierSelect
          name="supplierGuids"
          label=""
          control={control}
          placeholder="Search supplier name"
        />
      </Section>
    </form>
  );
}

const Section = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: 16,
  padding: 24,
  borderBottom: `1px solid ${theme.colors.sysBorderDecorative}`,
}));

const SectionTitle = styled(Text)({
  fontSize: 14,
  lineHeight: "125%",
});

const Divider = styled.hr(({ theme }) => ({
  width: "100%",
  margin: 0,
  border: 0,
  borderTop: `1px solid ${theme.colors.sysBorderDecorative}`,
}));

export default SpendDataFilters;
